/** Shared helpers for Automation Cases UI (Phase 2.4A Step 3). */

export const POLL_MS = 2500;

export const MULTIPLE_SECTIONS_WARNING =
  'Multiple test-like sections detected. This upload will be treated as one Automation Case.';

/** display_status values that indicate an in-flight generation or execution job. */
export const TRANSIENT_DISPLAY_STATUSES = new Set([
  'Generation Queued',
  'Generating',
  'Execution Queued',
  'Execution Running',
]);

/** Stop polling when display_status reaches one of these. */
export const TERMINAL_DISPLAY_STATUSES = new Set([
  'Generated',
  'Generation Failed',
  'Execution Passed',
  'Execution Failed',
  'Obsolete',
  'Discarded',
  'Draft',
  'Analyzed',
  'Needs Clarification',
  'Ready for Approval',
  'Approved for Automation',
  'Not Approved',
]);

export const ACTION_LABELS = {
  analyze: 'Analyze',
  approve: 'Approve',
  approve_with_assumptions: 'Approve with assumptions',
  generate: 'Generate Test Cases',
  run: 'Run',
  run_again: 'Run Again',
  mark_obsolete: 'Mark Obsolete',
  view_details: 'View Details',
  view_generated_files: 'View Generated Files',
  view_execution_history: 'View Execution History',
  view_failure: 'View Failure',
  discard_artifacts: 'Discard Artifacts',
};

/** Backend may expose actions not yet implemented in the UI — hide from active controls. */
export const UNSUPPORTED_UI_ACTIONS = new Set(['discard_artifacts', 'create_pr']);

export function filterUiActions(allowedActions = []) {
  return (allowedActions || []).filter(
    (action) => Object.prototype.hasOwnProperty.call(ACTION_LABELS, action)
      && !UNSUPPORTED_UI_ACTIONS.has(action)
  );
}

export function isTransientDisplayStatus(displayStatus) {
  return TRANSIENT_DISPLAY_STATUSES.has(displayStatus);
}

export function formatSourceLabel(sourceType) {
  if (sourceType === 'txt_upload') return 'TXT upload';
  if (sourceType === 'paste') return 'Paste';
  return sourceType || '—';
}

export function formatReadinessLabel(readinessStatus) {
  if (!readinessStatus) return '—';
  return readinessStatus.replace(/_/g, ' ');
}

/** Table filter keys for Automation Cases list hygiene (Step 5). */
export const TABLE_FILTERS = {
  active: 'Active',
  needs_attention: 'Needs Attention',
  passed: 'Passed',
  failed: 'Failed',
  obsolete: 'Obsolete',
  all: 'All',
};

export const DEFAULT_TABLE_FILTER = 'active';

const POST_APPROVAL_DISPLAY_STATUSES = new Set([
  'Approved for Automation',
  'Generation Queued',
  'Generating',
  'Generated',
  'Generation Failed',
  'Execution Queued',
  'Execution Running',
  'Execution Passed',
  'Execution Failed',
]);

/** Lifecycle-aware Analysis column label (Option B). */
export function formatTableAnalysisLabel(caseRow) {
  if (!caseRow) return '—';
  const displayStatus = caseRow.display_status || '';
  if (displayStatus === 'Obsolete' || displayStatus === 'Discarded') return '—';

  const approved =
    caseRow.approval_status === 'approved' || POST_APPROVAL_DISPLAY_STATUSES.has(displayStatus);

  if (approved) {
    if (caseRow.readiness_status === 'ready_with_warnings') return 'Approved · warnings';
    return 'Approved';
  }

  if (caseRow.readiness_status) return formatReadinessLabel(caseRow.readiness_status);
  if (displayStatus === 'Needs Clarification') return 'needs clarification';
  if (displayStatus === 'Ready for Approval') return 'ready for approval';
  return '—';
}

export function formatCaseRowSubtitle(caseRow) {
  if (!caseRow) return '';
  const parts = [];
  if (caseRow.source_filename) {
    parts.push(caseRow.source_filename);
  } else if (caseRow.source_type && caseRow.source_type !== 'paste') {
    parts.push(formatSourceLabel(caseRow.source_type));
  }
  if (caseRow.created_at) {
    parts.push(`Created ${formatDateTime(caseRow.created_at)}`);
  } else if (caseRow.updated_at) {
    parts.push(`Updated ${formatDateTime(caseRow.updated_at)}`);
  }
  return parts.join(' · ');
}

export function matchesTableFilter(caseRow, filterKey) {
  if (!caseRow) return false;
  const status = caseRow.display_status || '';
  const lifecycle = caseRow.lifecycle_status || '';

  switch (filterKey) {
    case 'active':
      return lifecycle !== 'obsolete' && lifecycle !== 'discarded';
    case 'needs_attention':
      return caseRow.attention_required === true;
    case 'passed':
      return status === 'Execution Passed' || status === 'Generated';
    case 'failed':
      return status === 'Execution Failed' || status === 'Generation Failed';
    case 'obsolete':
      return status === 'Obsolete' || lifecycle === 'obsolete';
    case 'all':
      return true;
    default:
      return true;
  }
}

export function isDuplicateCreateError(err) {
  return err?.status === 409 && err?.detail?.code === 'duplicate_active_case';
}

export function getDuplicateExistingCase(err) {
  if (!isDuplicateCreateError(err)) return null;
  return err.detail?.existing_case || null;
}

/** Surface backend validation messages (422 structured detail or plain string). */
export function formatAutomationActionError(err, fallback = 'Action failed') {
  if (!err) return fallback;
  const detail = err.detail;
  if (detail && typeof detail === 'object' && detail.message) {
    return mapAutomationUserMessage(detail.message);
  }
  if (typeof detail === 'string' && detail.trim()) {
    return mapAutomationUserMessage(detail);
  }
  if (err.message && String(err.message).trim()) {
    return mapAutomationUserMessage(err.message);
  }
  return fallback;
}

export function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export function statusChipColor(displayStatus) {
  const status = displayStatus || '';
  if (status.includes('Passed') || status === 'Generated' || status === 'Approved for Automation') {
    return 'success';
  }
  if (status.includes('Failed') || status === 'Not Approved') {
    return 'error';
  }
  if (status.includes('Queued') || status.includes('Running') || status === 'Generating') {
    return 'info';
  }
  if (status === 'Needs Clarification' || status === 'Ready for Approval') {
    return 'warning';
  }
  if (status === 'Obsolete' || status === 'Discarded') {
    return 'default';
  }
  return 'default';
}

/** Human-readable PR publication status (separate from execution display_status). */
export function isMockPrEvidence(caseRow) {
  if (!caseRow) return false;
  if (caseRow.latest_pr_is_mock === true) return true;
  if (caseRow.latest_pr_is_mock === false) return false;
  const url = caseRow.latest_pr_url || '';
  return url.toLowerCase().includes('/pull/mock-');
}

export function formatPrStatusLabel(prStatus, { isMock = false } = {}) {
  const status = (prStatus || '').toLowerCase();
  if (!status) return null;
  if (status === 'open') return isMock ? 'Mock PR' : 'PR Open';
  if (status === 'queued') return isMock ? 'Mock PR Queued' : 'PR Queued';
  if (status === 'running') return isMock ? 'Mock PR Running' : 'PR Running';
  if (status === 'validation_failed') return 'PR Validation Failed';
  if (status === 'failed') return 'PR Failed';
  if (status === 'closed') return isMock ? 'Mock PR Closed' : 'PR Closed';
  return `PR ${status.replace(/_/g, ' ')}`;
}

export function prStatusChipColor(prStatus, { isMock = false } = {}) {
  const status = (prStatus || '').toLowerCase();
  if (status === 'open') return isMock ? 'default' : 'success';
  if (status === 'queued' || status === 'running') return 'info';
  if (status === 'validation_failed' || status === 'failed') return 'warning';
  if (status === 'closed') return 'default';
  return 'default';
}

export function formatPrSectionTitle(caseRow) {
  return isMockPrEvidence(caseRow) ? 'Mock Pull Request Evidence' : 'GitHub Pull Request';
}

export function formatPrLinkLabel(caseRow) {
  return isMockPrEvidence(caseRow) ? 'Mock link' : 'Open PR';
}

export function shouldShowPrLink(caseRow) {
  if (!caseRow?.latest_pr_url) return false;
  return !isMockPrEvidence(caseRow);
}

export function hasPrActivity(caseRow) {
  return Boolean(caseRow?.latest_pr_status);
}

const SENSITIVE_CASE_KEYS = new Set([
  'raw_input_encrypted',
  'token',
  'token_encrypted',
  'github_token',
  'authorization',
  'pat',
  'password',
  'metadata_json',
]);

export function pickPrimaryActions(allowedActions = []) {
  const actions = filterUiActions(allowedActions);
  const priority = ['run_again', 'run', 'generate', 'approve', 'analyze'];
  for (const action of priority) {
    if (actions.includes(action)) return action;
  }
  return actions.find((a) => a.startsWith('view_')) || null;
}

export function extractReadinessSummary(readinessSnapshot) {
  if (!readinessSnapshot) return null;
  const reports = readinessSnapshot.reports || [];
  const report = reports[0] || readinessSnapshot;
  if (!report || typeof report !== 'object') return null;
  return {
    status: report.status,
    summary: report.summary,
    blockers: report.missing_information || [],
    appArea: report.detected_app_area || null,
    warnings: [
      ...(report.automation_risks || []),
      ...(report.credential_warning ? ['Credentials referenced in manual input'] : []),
    ],
  };
}

export const MINOR_CLARIFICATION_BANNER =
  'Minor clarification questions remain. You can add answers or approve with assumptions to generate a best-effort automation draft.';

export const HARD_BLOCK_BANNER = 'Generation blocked — required context missing';

export const GENERIC_BEST_EFFORT_PRE_APPROVAL_BANNER =
  'No deterministic template exists for this app area yet. Stratovai can still generate a best-effort automation draft from the provided steps after approval with assumptions.';

export const GENERIC_BEST_EFFORT_POST_APPROVAL_BANNER =
  'Generating with generic best-effort Playwright mode because no deterministic template exists for this app area.';

export const NON_DETERMINISTIC_APP_AREAS = new Set(['test_case_generation', 'ai_chat']);

const LEGACY_UNSUPPORTED_APP_AREA_RE =
  /app area '.+' is not supported for deterministic playwright generation/i;

export function isLegacyUnsupportedAppAreaError(message) {
  return LEGACY_UNSUPPORTED_APP_AREA_RE.test(message || '');
}

export function mapAutomationUserMessage(message) {
  if (!message) return message;
  if (isLegacyUnsupportedAppAreaError(message)) {
    return GENERIC_BEST_EFFORT_PRE_APPROVAL_BANNER;
  }
  return message;
}

export function extractPlanSummary(planSnapshot) {
  if (!planSnapshot) return null;
  const plan = planSnapshot.automation_plan || planSnapshot;
  if (!plan) return null;
  return {
    status: plan.automation_plan_status,
    readiness: plan.generation_readiness,
    framework: plan.recommended_framework,
    dependencies: plan.external_dependencies || plan.risk_flags || [],
    cleanup: plan.cleanup_strategy,
    structure: plan.suggested_test_structure,
    clarificationQuestions: plan.clarification_questions || [],
    appArea: plan.detected_app_area || planSnapshot.detected_app_area || null,
  };
}

export function getGenerationGatingState(caseDetail) {
  const plan = extractPlanSummary(caseDetail?.automation_plan_snapshot_json);
  const readinessSummary = extractReadinessSummary(caseDetail?.readiness_snapshot_json);
  const readiness = plan?.readiness;
  const approved = caseDetail?.approval_status === 'approved';
  const questions = plan?.clarificationQuestions || [];
  const appArea = plan?.appArea || readinessSummary?.appArea;
  const genericFallback = appArea && NON_DETERMINISTIC_APP_AREAS.has(appArea);

  if (!readiness) {
    return { kind: 'unknown', banner: null, approveLabel: null };
  }

  if (readiness === 'do_not_generate_yet' || plan?.status === 'plan_not_recommended') {
    return {
      kind: 'hard_blocked',
      banner: HARD_BLOCK_BANNER,
      blockers: readinessSummary?.blockers || [],
      approveLabel: null,
    };
  }

  if (
    !approved
    && readiness === 'can_generate_after_minor_clarification'
    && questions.length > 0
  ) {
    return {
      kind: 'minor_clarification',
      banner: MINOR_CLARIFICATION_BANNER,
      approveLabel: 'Approve with assumptions',
    };
  }

  if (!approved && genericFallback) {
    return {
      kind: 'generic_best_effort',
      banner: GENERIC_BEST_EFFORT_PRE_APPROVAL_BANNER,
      approveLabel: 'Approve with assumptions',
    };
  }

  if (approved && genericFallback) {
    return {
      kind: 'generic_approved',
      banner: GENERIC_BEST_EFFORT_POST_APPROVAL_BANNER,
      approveLabel: null,
    };
  }

  if (readiness === 'can_generate_now' || readiness === 'can_generate_after_minor_clarification') {
    return {
      kind: 'ready',
      banner: null,
      approveLabel: approved ? null : 'Approve',
    };
  }

  return { kind: 'unknown', banner: null, approveLabel: null };
}

export function resolveActionLabel(action, gatingState) {
  if (action === 'approve' && gatingState?.approveLabel) {
    return gatingState.approveLabel;
  }
  return ACTION_LABELS[action] || action;
}

/** Ensure sensitive encrypted fields never reach the DOM. */
export function sanitizeCaseForDisplay(caseDetail) {
  if (!caseDetail || typeof caseDetail !== 'object') return caseDetail;
  const safe = {};
  for (const [key, value] of Object.entries(caseDetail)) {
    if (SENSITIVE_CASE_KEYS.has(key)) continue;
    safe[key] = value;
  }
  return safe;
}
